// frontend/packages/business-console/src/pages/PlanFeatures.jsx
//
// Read-only "Plan & features" panel. Shows the tenant which operator-controlled
// features are on for them. Deliberately NOT editable here: the UpRyzze team
// manages these per client in UpRyzze Tools, so the client sees the state and
// contacts us to change it. Rendered inside the Settings hub.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';

import { getMyFeatures } from '../services/featuresService';
import { useTokens, PageHeader, Surface } from '../components/primitives';

export default function PlanFeatures() {
  const t = useTokens();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [features, setFeatures] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await getMyFeatures();
        if (alive) setFeatures(list);
      } catch (err) {
        if (alive)
          setError(
            err?.response?.data?.message || 'Could not load your features.',
          );
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Box>
      <PageHeader
        title="Plan & features"
        meta={
          <Typography sx={{ color: t.muted, fontSize: 13.5 }}>
            {"What's turned on for your business. Contact us at "}
            support@upryzze.com to add or change anything.
          </Typography>
        }
        sx={{ mb: 2 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={26} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : features.length === 0 ? (
        <Surface sx={{ p: 3 }}>
          <Typography sx={{ color: t.muted }}>
            No optional features to show yet.
          </Typography>
        </Surface>
      ) : (
        <Stack spacing={1.25}>
          {features.map((f) => (
            <Surface
              key={f.key}
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <Box
                sx={{ mt: 0.25, color: f.enabled ? 'success.main' : t.faint }}
              >
                {f.enabled ? (
                  <CheckCircleOutlineOutlinedIcon fontSize="small" />
                ) : (
                  <RadioButtonUncheckedOutlinedIcon fontSize="small" />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700 }}>{f.label}</Typography>
                <Typography sx={{ color: t.muted, fontSize: 13.5, mt: 0.25 }}>
                  {f.description}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={f.enabled ? 'On' : 'Off'}
                color={f.enabled ? 'success' : 'default'}
                variant={f.enabled ? 'filled' : 'outlined'}
              />
            </Surface>
          ))}
        </Stack>
      )}
    </Box>
  );
}
